import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Grid from 'shopper/components/Grid';
import Cell from 'shopper/components/Grid/Cell';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import { sendEvent } from 'lib/gtag';
import {
  toCategoryLink,
  toLandingPageLink,
  toStoreCouponsLink,
  toSubcategoryLink,
} from 'lib/links';

import { useModal } from 'providers/ModalProvider';
import { useTopbar } from 'providers/TopbarProvider';

import Anchor from '../Anchor';
import Image from '../Image';
import WhatIsPromobitDescription from './WhatIsPromobitDescription';

import APP from 'constants/app';
import FOOTER from 'constants/footer';
import LANDINGS from 'constants/landings';
import LINKS from 'constants/links';
import MOBILE_APPS from 'constants/mobileApp';
import SOCIAL_MEDIA from 'constants/socialMedia';
import WISHLIST from 'constants/wishlist';

const WishlistModal = dynamic(() => import('../Wishlist/WishlistModal'), {
  ssr: false,
});

const APPS_GA_EVENT_CATEGORY = 'footer_app';

const SOCIAL_MEDIA_LIST = [
  {
    name: SOCIAL_MEDIA.FACEBOOK.TITLE,
    href: SOCIAL_MEDIA.FACEBOOK.URL,
    icon: <Icon name="facebook" aria-hidden />,
  },
  {
    name: SOCIAL_MEDIA.TWITTER.TITLE,
    href: SOCIAL_MEDIA.TWITTER.URL,
    icon: <Icon name="twitter" aria-hidden />,
  },
  {
    name: SOCIAL_MEDIA.INSTAGRAM.TITLE,
    href: SOCIAL_MEDIA.INSTAGRAM.URL,
    icon: <Icon name="instagram" aria-hidden />,
  },
  {
    name: SOCIAL_MEDIA.LINKEDIN.TITLE,
    href: SOCIAL_MEDIA.LINKEDIN.URL,
    icon: <Icon name="linkedin" aria-hidden />,
  },
  {
    name: SOCIAL_MEDIA.TELEGRAM.TITLE,
    href: SOCIAL_MEDIA.TELEGRAM.URL,
    icon: <Icon name="telegram" aria-hidden />,
  },
];

const INSTITUTIONAL_LINKS_LIST = [
  { name: FOOTER.INSTITUTIONAL.PAGES.ABOUT_US, href: LINKS.ABOUT_US },
  { name: FOOTER.INSTITUTIONAL.PAGES.BUSINESS, href: LINKS.BUSINESS },
  {
    name: FOOTER.INSTITUTIONAL.PAGES.CONTACT_US,
    href: toLandingPageLink('contato', LANDINGS.TYPES.INSTITUTIONAL),
  },
  { name: FOOTER.INSTITUTIONAL.PAGES.CAREERS, href: LINKS.WORK_WITH_US },
  { name: FOOTER.INSTITUTIONAL.PAGES.MEDIA, href: LINKS.MEDIA },
];

const CASH3_LINKS_LIST = [
  { name: FOOTER.CASH3.PAGES.PROMOBIT, href: APP.URL },
  { name: FOOTER.CASH3.PAGES.IDINHEIRO, href: LINKS.IDINHEIRO },
  { name: FOOTER.CASH3.PAGES.MELIUZ, href: LINKS.MELIUZ },
  { name: FOOTER.CASH3.PAGES.MELHOR_PLANO, href: LINKS.MELHOR_PLANO },
  { name: FOOTER.CASH3.PAGES.MINHA_CONEXAO, href: LINKS.MINHA_CONEXAO },
  { name: FOOTER.CASH3.PAGES.PICODI_BR, href: LINKS.PICODI_BR },
];

const SPECIAL_PAGES_LINKS_LIST = [
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.BLACK_FRIDAY,
    href: toLandingPageLink('black-friday'),
  },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.AMERICANAS_COUPONS,
    href: toStoreCouponsLink('americanas'),
  },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.SMARTPHONE_DEALS,
    href: toCategoryLink('smartphones-tablets-e-telefones'),
  },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.EARPHONE_DEALS,

    href: toSubcategoryLink('fone-de-ouvido'),
  },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.GAMES_DEALS,
    href: toCategoryLink('games'),
  },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.FRIDGE_DEALS,
    href: toSubcategoryLink('geladeiras'),
  },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.PONTOFRIO_COUPONS,
    href: toStoreCouponsLink('ponto-frio'),
  },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.PAMPERS_NAPPIES_DEALS,
    href: toSubcategoryLink('fralda-pampers'),
  },
  { name: FOOTER.SPECIAL_PAGES.PAGES.TV_DEALS, href: toSubcategoryLink('tv') },
  {
    name: FOOTER.SPECIAL_PAGES.PAGES.SUBMARINO_COUPONS,
    href: toStoreCouponsLink('submarino'),
  },
];

const HELP_LINKS_LIST = [
  {
    name: FOOTER.HELP.ARTICLES.COMMUNITY_GUIDELINES,
    href: LINKS.COMMUNITY_GUIDELINES,
  },
  {
    name: FOOTER.HELP.ARTICLES.MOD_CRITERIA,
    href: toLandingPageLink(
      'criterios-de-moderacao',
      LANDINGS.TYPES.INSTITUTIONAL
    ),
  },
  { name: FOOTER.HELP.ARTICLES.FAQ, href: LINKS.FAQ },
  { name: FOOTER.HELP.ARTICLES.PURCHASE_ISSUES, href: LINKS.PURCHASE_ISSUES },
];

const onPlayStoreClick = () =>
  sendEvent({ category: APPS_GA_EVENT_CATEGORY, action: 'google_play' });

const onAppStoreClick = () =>
  sendEvent({ category: APPS_GA_EVENT_CATEGORY, action: 'apple_store' });

const FooterMain = ({ withCash3Section }) => {
  const { pathname } = useRouter();
  const { featuredLanding } = useTopbar();
  const { showModal } = useModal();

  return (
    <Grid
      mx="auto"
      gridAutoFlow="column"
      mb={6}
      mt={[6, null, 8]}
      maxWidth={[null, null, '64rem', '80rem']}
      px={[2, 4, 7, 6, 4]}
      gridColumnGap={[4, null, null, 6, 4]}
    >
      <Cell
        height="fit-content"
        gridColumnStart={1}
        gridColumnEnd={['span 1', 'span 4', null, 'span 3']}
      >
        <Flex flexDirection="column" display={['none', null, 'flex']}>
          <Heading
            size="size5"
            as="p"
            mb={3}
            fontSize={[18, null, null, 16]}
            textAlign={['center', null, 'left']}
            bold
          >
            {FOOTER.WHAT_IS.TITLE}
          </Heading>
          <WhatIsPromobitDescription />
        </Flex>
        {pathname !== LINKS.HOME && (
          <Flex flexDirection="column" mt={7} display={['none', null, 'flex']}>
            <Heading
              size="size5"
              as="p"
              mb={3}
              fontSize={[18, null, null, 16]}
              textAlign={['center', null, 'left']}
              bold
            >
              {WISHLIST.TITLE}
            </Heading>
            <Text size="size1" fontSize={[16, null, null, 14]} mb={4}>
              {WISHLIST.DEFINITION}
            </Text>
            <Button
              type="subtle"
              size="size2"
              iconLeft={<Icon name="heart" />}
              full
              onClick={() => showModal(WishlistModal, { isOpen: true })}
            >
              {WISHLIST.TITLE}
            </Button>
          </Flex>
        )}
      </Cell>
      <Cell
        height="fit-content"
        gridColumnEnd={['span 4', null, null, 'span 3']}
        gridColumnStart={['1', null, null, '4', '5']}
        marginTop={[null, null, 7, 0, null]}
      >
        <Flex flexDirection="column" mb={7}>
          <Heading
            size="size5"
            as="p"
            mb={[4, null, null, 3]}
            fontSize={[18, null, null, 16]}
            textAlign={['center', null, 'left']}
            bold
          >
            {FOOTER.INSTITUTIONAL.TITLE}
          </Heading>
          <Flex
            flexDirection="column"
            flexWrap="wrap"
            justifyContent="flex-start"
            alignItems={['center', null, 'flex-start']}
          >
            {INSTITUTIONAL_LINKS_LIST.map(({ name, href }) => (
              <Link
                size="size1"
                type="secondary"
                key={name}
                href={href}
                py={[2, null, null, 0]}
                mb={[null, null, null, 1]}
                lineHeight={['2rem', null, null, '1.25rem']}
                fontSize={[16, null, null, 14]}
              >
                {name}
              </Link>
            ))}
          </Flex>
        </Flex>
        {withCash3Section && (
          <Flex
            flexDirection="column"
            mb={7}
            alignItems={['center', null, 'flex-start']}
          >
            <Heading
              size="size5"
              as="p"
              mb={3}
              fontSize={[18, null, null, 16]}
              textAlign={['center', null, 'left']}
              bold
            >
              {FOOTER.CASH3.TITLE}
            </Heading>
            <Flex
              flexWrap="wrap"
              flexDirection="column"
              justifyContent="flex-start"
              alignItems={['center', null, 'flex-start']}
            >
              {CASH3_LINKS_LIST.map(({ name, href }) => (
                <Link
                  size="size1"
                  type="secondary"
                  key={name}
                  href={href}
                  mb={[null, null, null, 1]}
                  lineHeight={['2rem', null, null, '1.25rem']}
                  fontSize={[16, null, null, 14]}
                >
                  {name}
                </Link>
              ))}
            </Flex>
          </Flex>
        )}
        <Flex
          flexDirection="column"
          mb={7}
          alignItems={['center', null, 'flex-start']}
        >
          <Heading
            size="size5"
            as="p"
            mb={[4, null, null, 3]}
            fontSize={[18, null, null, 16]}
            textAlign={['center', null, 'left']}
            bold
          >
            {FOOTER.SECURE_SITE.TITLE}
          </Heading>
          <Flex
            alignItems="center"
            flexDirection={!withCash3Section ? [null, null, 'column'] : 'row'}
          >
            <Image
              width="114px"
              height="33px"
              src={`${APP.LOCAL_IMAGE_PATH}/features/footer/badge-google-safe-browsing.png`}
              alt={FOOTER.SECURE_SITE.GOOGLE_SAFE_BROWSING_ALT}
            />
            <Image
              width="110px"
              height="40px"
              mt={!withCash3Section && [null, null, 2]}
              ml={withCash3Section ? 4 : [4, null, 0]}
              src={`${APP.LOCAL_IMAGE_PATH}/features/footer/badge-google-for-startups-accelerator-2020.png`}
              alt={FOOTER.SECURE_SITE.GOOGLE_FOR_STARTUPS_ALT}
            />
          </Flex>
        </Flex>
      </Cell>
      <Cell
        height="fit-content"
        gridColumnEnd={['span 4', null, null, 'span 3']}
        gridColumnStart={['1', null, '7', null, '9']}
      >
        <Flex flexDirection="column" mb={7}>
          <Heading
            size="size5"
            as="p"
            fontSize={[18, null, null, 16]}
            textAlign={['center', null, 'left']}
            mb={[4, null, null, 3]}
            bold
          >
            {FOOTER.SPECIAL_PAGES.TITLE}
          </Heading>
          <Flex
            flexDirection="column"
            flexWrap="wrap"
            justifyContent="flex-start"
            alignItems={['center', null, 'flex-start']}
          >
            {featuredLanding && (
              <Link
                size="size1"
                type="secondary"
                key={featuredLanding.landingId}
                href={toLandingPageLink(
                  featuredLanding.landingSlug,
                  featuredLanding.landingType
                )}
                py={[2, null, null, 0]}
                mb={[null, null, null, 1]}
                lineHeight={['2rem', null, null, '1.25rem']}
                fontSize={[16, null, null, 14]}
              >
                {featuredLanding.landingName}
              </Link>
            )}
            {SPECIAL_PAGES_LINKS_LIST.map(({ name, href }) => {
              if (
                featuredLanding &&
                href ===
                  toLandingPageLink(
                    featuredLanding.landingSlug,
                    featuredLanding.landingType
                  )
              ) {
                return null;
              }

              return (
                <Link
                  size="size1"
                  type="secondary"
                  key={name}
                  href={href}
                  py={[2, null, null, 0]}
                  mb={[null, null, null, 1]}
                  lineHeight={['2rem', null, null, '1.25rem']}
                  fontSize={[16, null, null, 14]}
                >
                  {name}
                </Link>
              );
            })}
          </Flex>
        </Flex>
      </Cell>
      <Cell
        height="fit-content"
        gridColumnEnd={['span 4', null, null, 'span 3']}
        gridColumnStart={['1', null, '7', '10', '13']}
        marginTop={[null, null, 7, 0, null]}
      >
        <Flex flexDirection="column" mb={7}>
          <Heading
            size="size5"
            as="p"
            mb={[4, null, null, 3]}
            fontSize={[18, null, null, 16]}
            textAlign={['center', null, 'left']}
            bold
          >
            {FOOTER.HELP.TITLE}
          </Heading>
          <Flex
            flexDirection="column"
            flexWrap="wrap"
            justifyContent="flex-start"
            alignItems={['center', null, 'flex-start']}
          >
            {HELP_LINKS_LIST.map(({ name, href }) => (
              <Link
                size="size1"
                type="secondary"
                key={name}
                href={href}
                py={[2, null, null, 0]}
                mb={[null, null, null, 1]}
                lineHeight={['2rem', null, null, '1.25rem']}
                fontSize={[16, null, null, 14]}
              >
                {name}
              </Link>
            ))}
          </Flex>
        </Flex>
        <Flex flexDirection="column" mb={6}>
          <Heading
            size="size5"
            as="p"
            fontSize={[18, null, null, 16]}
            mb={[4, null, null, 3]}
            textAlign={['center', null, 'left']}
            bold
          >
            {MOBILE_APPS.TITLE}
          </Heading>
          <Flex
            flexDirection={[null, null, 'column']}
            justifyContent={['center', null, 'flex-start']}
            alignItems={['center', null, 'flex-start']}
          >
            <Anchor
              target="_blank"
              rel="noreferrer"
              href={MOBILE_APPS.ANDROID.APP_URL}
              onClick={onPlayStoreClick}
            >
              <Image
                height="36px"
                width="116px"
                src={`${APP.LOCAL_IMAGE_PATH}/features/footer/badge-google-play.svg`}
                alt={MOBILE_APPS.ANDROID.DESCRIPTION}
              />
            </Anchor>
            <Anchor
              target="_blank"
              rel="noreferrer"
              href={MOBILE_APPS.IOS.APP_URL}
              mt={[null, null, 2]}
              ml={[4, null, 0]}
              onClick={onAppStoreClick}
            >
              <Image
                height="37px"
                width="123px"
                src={`${APP.LOCAL_IMAGE_PATH}/features/footer/badge-apple-store.svg`}
                alt={MOBILE_APPS.IOS.DESCRIPTION}
              />
            </Anchor>
          </Flex>
        </Flex>
        <Flex flexDirection="column">
          <Heading
            size="size5"
            as="p"
            mb={[4, null, null, 3]}
            fontSize={[18, null, null, 16]}
            textAlign={['center', null, 'left']}
            bold
          >
            {SOCIAL_MEDIA.FOLLOW_US}
          </Heading>
          <Flex
            flexDirection="row"
            justifyContent={['center', null, 'flex-start']}
          >
            {SOCIAL_MEDIA_LIST.map(({ name, href, icon }) => (
              <Button
                as="a"
                rel="noreferrer"
                target="_blank"
                size="size1"
                type="ghost"
                key={name}
                href={href}
                icon={icon}
                aria-label={`${name} do Promobit`}
              />
            ))}
          </Flex>
        </Flex>
      </Cell>
    </Grid>
  );
};

FooterMain.defaultProps = {
  withCash3Section: false,
};

FooterMain.propTypes = {
  withCash3Section: PropTypes.bool,
};

export default FooterMain;
